import { CartAddEvent } from '@theme/events';

const SESSION_KEY_PREFIX = 'drw_overlay_seen_';
const HOLD_KEY = 'drw_upgrade_hold';
const HOST_CLASS = 'cart-drawer-overlay-host';
const HOLD_NOTE_CLASS = 'drw-upgrade-hold';
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

class CartDrawerOverlayManager {
  constructor() {
    /** @type {HTMLElement | null} */
    this.dataRoot = document.getElementById('cart-drawer-overlays-data');
    if (!this.dataRoot) return;

    this.oncePerSession = this.dataRoot.dataset.oncePerSession === 'true';

    /** @type {Map<string, HTMLTemplateElement>} */
    this.templatesByTrigger = new Map();
    for (const tpl of this.dataRoot.querySelectorAll('template.cart-drawer-overlay-template')) {
      const ids = (tpl.dataset.triggerProductIds || '').split(',').map((s) => s.trim()).filter(Boolean);
      for (const id of ids) {
        // An upgrade offer outranks a plain add-on offer for the same trigger product.
        const existing = this.templatesByTrigger.get(id);
        if (existing?.dataset.overlayType === 'upgrade_swap' && tpl.dataset.overlayType !== 'upgrade_swap') continue;
        this.templatesByTrigger.set(id, tpl);
      }
    }

    document.addEventListener(CartAddEvent.eventName, this.handleCartEvent);

    this.lastSeenProductId = null;
    this.upgradeTimer = null;
  }

  handleCartEvent = (event) => {
    const productId = event?.detail?.data?.productId;
    if (!productId) return;
    if (productId === this.lastSeenProductId) return;
    this.lastSeenProductId = productId;

    const tpl = this.templatesByTrigger.get(String(productId));
    if (!tpl) return;

    const blockId = tpl.dataset.blockId;
    if (this.oncePerSession && sessionStorage.getItem(SESSION_KEY_PREFIX + blockId)) return;

    // Wait one frame so cart-drawer's auto-open finishes mounting the dialog.
    requestAnimationFrame(() => this.showOverlay(tpl));
  };

  showOverlay(template) {
    const drawer = document.querySelector('cart-drawer-component .cart-drawer__inner');
    if (!drawer) return;

    let host = drawer.querySelector('.' + HOST_CLASS);
    if (!host) {
      host = document.createElement('div');
      host.className = HOST_CLASS;
      host.setAttribute('aria-hidden', 'true');
      drawer.appendChild(host);
    }

    host.innerHTML = '';
    const fragment = template.content.cloneNode(true);
    host.appendChild(fragment);
    host.dataset.blockId = template.dataset.blockId;
    host.dataset.overlayType = template.dataset.overlayType;
    host.setAttribute('aria-hidden', 'false');

    if (template.dataset.overlayType === 'bundle') {
      this.bindBundle(host, template);
    }

    if (template.dataset.overlayType === 'upgrade_swap') {
      this.bindUpgrade(host, template);
    }

    this.bindActions(host);

    // Make sure cart drawer is visible
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      const dialog = cartDrawer.querySelector('.cart-drawer__dialog');
      if (dialog && !dialog.open) cartDrawer.open();
    }
  }

  hideOverlay(host) {
    if (!host) return;
    clearInterval(this.upgradeTimer);
    const blockId = host.dataset.blockId;
    if (this.oncePerSession && blockId) {
      sessionStorage.setItem(SESSION_KEY_PREFIX + blockId, '1');
    }
    host.setAttribute('aria-hidden', 'true');
    host.innerHTML = '';
  }

  bindActions(host) {
    // The host element is reused across overlays; bind its click handler once.
    if (host.dataset.bound) return;
    host.dataset.bound = 'true';

    host.addEventListener('click', async (e) => {
      const actionEl = e.target.closest('[data-overlay-action]');
      if (!actionEl) return;
      const action = actionEl.dataset.overlayAction;

      if (action === 'dismiss') {
        this.hideOverlay(host);
        return;
      }

      if (action === 'add') {
        const variantIds = (actionEl.dataset.variantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
        if (!variantIds.length) return;
        await this.addVariants(actionEl, variantIds);
        this.hideOverlay(host);
      }

      if (action === 'add-bundle') {
        const checked = host.querySelectorAll('.cart-drawer-overlay__checkbox:checked');
        const ids = Array.from(checked).map((c) => c.dataset.variantId).filter(Boolean);
        if (!ids.length) return;
        await this.addVariants(actionEl, ids);
        this.hideOverlay(host);
      }

      if (action === 'upgrade') {
        await this.swapToUpgrade(actionEl, host);
      }
    });
  }

  async addVariants(button, variantIds) {
    const original = button.innerHTML;
    button.disabled = true;
    button.classList.add('is-loading');
    try {
      const items = variantIds.map((id) => ({ id: Number(id), quantity: 1 }));
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      // Notify the rest of the theme (refreshes drawer contents).
      document.dispatchEvent(new CartAddEvent(undefined, undefined, { source: 'cart-drawer-overlay' }));
    } catch (err) {
      console.error('[cart-drawer-overlay] Add failed', err);
      button.innerHTML = original;
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  /** Runs the offer countdown; the overlay closes itself when the window ends. */
  bindUpgrade(host, template) {
    const seconds = Number(template.dataset.timerSeconds || 600);
    const deadline = Date.now() + seconds * 1000;
    host.dataset.deadline = String(deadline);
    const timerEls = host.querySelectorAll('[data-upgrade-timer]');

    const tick = () => {
      const left = deadline - Date.now();
      if (left <= 0) {
        this.hideOverlay(host);
        return;
      }
      const text = formatClock(left);
      timerEls.forEach((el) => (el.textContent = text));
    };

    clearInterval(this.upgradeTimer);
    tick();
    this.upgradeTimer = setInterval(tick, 1000);
  }

  /**
   * Swaps one unit of the trigger product for the upgrade product, applies the
   * upgrade code, and leaves the shopper in the refreshed cart drawer.
   */
  async swapToUpgrade(button, host) {
    const { variantId, productId, discountCode, triggerProductIds } = button.dataset;
    const deadline = Number(host.dataset.deadline || 0);
    if (!variantId || Date.now() >= deadline) {
      this.hideOverlay(host);
      return;
    }
    const triggerIds = (triggerProductIds || '').split(',').map((s) => s.trim()).filter(Boolean);

    button.disabled = true;
    button.classList.add('is-loading');
    try {
      const cart = await cartRequest('cart.js');
      const tradeIn = cart.items.find((item) => triggerIds.includes(String(item.product_id)));

      await cartRequest('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] });

      const update = {};
      if (tradeIn) update.updates = { [tradeIn.key]: tradeIn.quantity - 1 };
      if (discountCode) update.discount = discountCode;
      const updated = await cartRequest('cart/update.js', update);

      if (discountCode) writeHold({ code: discountCode, productId, deadline });
      this.hideOverlay(host);
      // Re-renders the drawer's line items and the header cart count in place.
      document.dispatchEvent(
        new CartAddEvent(updated, undefined, { source: 'cart-drawer-overlay', itemCount: updated.item_count })
      );
      new UpgradeHold();
    } catch (err) {
      console.error('[cart-drawer-overlay] Upgrade failed', err);
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  bindBundle(host, template) {
    const tierPercents = {
      2: parseFloat(template.dataset.pct2 || '0'),
      3: parseFloat(template.dataset.pct3 || '0'),
      4: parseFloat(template.dataset.pct4 || '0'),
    };
    const ctaTpl = template.dataset.ctaTemplate || 'Add %count% items for %total%';

    const checkboxes = Array.from(host.querySelectorAll('.cart-drawer-overlay__checkbox'));
    const totalEl = host.querySelector('[data-bundle-total]');
    const compareEl = host.querySelector('[data-bundle-compare]');
    const ctaLabel = host.querySelector('.cart-drawer-overlay__cta-label');
    const ctaButton = host.querySelector('[data-overlay-action="add-bundle"]');

    /** Tier % for a given checked count (clamped to 4). */
    const tierFor = (count) => tierPercents[Math.min(count, 4)] || 0;

    /** Apply percent-off discount to a cents value. Pure function. */
    const applyDiscount = (cents, pct) =>
      pct > 0 ? Math.round(cents * (1 - pct / 100)) : cents;

    /** Read a row's prices (in cents) from its checkbox dataset. */
    const readRow = (cb) => ({
      checked: cb.checked,
      price: Number(cb.dataset.price || 0),
      compareAt: Number(cb.dataset.compareAt || 0),
      li: cb.closest('.cart-drawer-overlay__item'),
    });

    /** What this row should display, given the current tier %. */
    const computeRow = (row, pct) => {
      const final = row.checked ? applyDiscount(row.price, pct) : row.price;
      // Strike = compare_at if it's higher than final; else show row.price as
      // strike only when a tier discount is actually being applied.
      let strike = 0;
      if (row.compareAt > final) strike = row.compareAt;
      else if (row.checked && pct > 0 && row.price > final) strike = row.price;
      return { final, strike };
    };

    /** Write final + strike text into the row's DOM. */
    const renderRow = (row, { final, strike }) => {
      const finalEl = row.li?.querySelector('[data-item-discounted]');
      const strikeEl = row.li?.querySelector('[data-item-compare]');
      if (finalEl) finalEl.textContent = formatMoney(final);
      if (strikeEl) {
        strikeEl.hidden = strike <= 0;
        if (strike > 0) strikeEl.textContent = formatMoney(strike);
      }
    };

    const recompute = () => {
      const rows = checkboxes.map(readRow);
      const checkedCount = rows.filter((r) => r.checked).length;
      const pct = tierFor(checkedCount);

      let totalFinal = 0;
      let totalStrike = 0;

      for (const row of rows) {
        const { final, strike } = computeRow(row, pct);
        renderRow(row, { final, strike });
        if (row.checked) {
          totalFinal += final;
          totalStrike += row.compareAt > 0 ? row.compareAt : row.price;
        }
      }

      if (totalEl) totalEl.textContent = formatMoney(totalFinal);
      if (compareEl) {
        compareEl.hidden = totalStrike <= totalFinal;
        if (totalStrike > totalFinal) compareEl.textContent = formatMoney(totalStrike);
      }
      if (ctaLabel) {
        ctaLabel.textContent = ctaTpl
          .replaceAll('%count%', String(checkedCount))
          .replaceAll('%total%', formatMoney(totalFinal));
      }
      if (ctaButton) ctaButton.disabled = checkedCount === 0;
    };

    checkboxes.forEach((c) => c.addEventListener('change', recompute));
    recompute();
  }
}

/**
 * Keeps an accepted upgrade price honest: shows the remaining window next to
 * every checkout button, and takes the upgrade code off the cart when it ends.
 */
class UpgradeHold {
  constructor() {
    this.hold = readHold();
    if (!this.hold) return;

    this.text = '';
    this.tick();
    if (!this.hold) return;
    this.interval = setInterval(() => this.tick(), 1000);
    this.observer = new MutationObserver(() => this.renderNotes());
    this.observer.observe(document.body, { childList: true, subtree: true });

    // The shopper may have removed the upgrade product since switching.
    cartRequest('cart.js')
      .then((cart) => {
        const inCart = cart.items.some((item) => String(item.product_id) === String(this.hold?.productId));
        if (!inCart) this.stop();
      })
      .catch(() => {});
  }

  tick() {
    const left = this.hold.deadline - Date.now();
    if (left <= 0) {
      this.expire();
      return;
    }
    this.text = formatClock(left);
    this.renderNotes();
  }

  renderNotes() {
    if (!this.hold) return;
    const label = `Pro upgrade price held for ${this.text}`;
    for (const button of document.querySelectorAll('#checkout, [name="checkout"]')) {
      let note = button.previousElementSibling;
      if (!note || !note.classList.contains(HOLD_NOTE_CLASS)) {
        note = document.createElement('p');
        note.className = HOLD_NOTE_CLASS;
        note.setAttribute('role', 'status');
        button.before(note);
      }
      if (note.textContent !== label) note.textContent = label;
    }
  }

  stop() {
    clearInterval(this.interval);
    this.observer?.disconnect();
    clearHold();
    document.querySelectorAll('.' + HOLD_NOTE_CLASS).forEach((note) => note.remove());
  }

  async expire() {
    const { code } = this.hold;
    this.stop();
    this.hold = null;
    try {
      const cart = await cartRequest('cart/update.js', {});
      const codes = Array.isArray(cart.discount_codes) ? cart.discount_codes.map((d) => d.code) : null;
      const same = (c) => c.toUpperCase() === code.toUpperCase();
      if (codes && !codes.some(same)) return;
      const updated = await cartRequest('cart/update.js', { discount: (codes || []).filter((c) => !same(c)).join(',') });
      if (/\/cart\/?$/.test(window.location.pathname)) {
        window.location.reload();
      } else {
        document.dispatchEvent(
          new CartAddEvent(updated, undefined, { source: 'cart-drawer-overlay', itemCount: updated.item_count })
        );
      }
    } catch (err) {
      console.error('[cart-drawer-overlay] Could not expire upgrade price', err);
    }
  }
}

async function cartRequest(path, body, attempt = 0) {
  const init =
    body === undefined
      ? { headers: { Accept: 'application/json' } }
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        };
  const res = await fetch(ROOT + path, init);
  // Shopify throttles bursts of cart calls (429); back off briefly and retry.
  if (res.status === 429 && attempt < 3) {
    await new Promise((resolve) => setTimeout(resolve, 800 * (attempt + 1)));
    return cartRequest(path, body, attempt + 1);
  }
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  // Some product text reaches the cart JSON with raw control characters, which
  // JSON.parse rejects. They only occur inside strings, so a space is a safe stand-in.
  const text = await res.text();
  return JSON.parse(text.replace(/[ -]+/g, ' '));
}

function readHold() {
  try {
    return JSON.parse(sessionStorage.getItem(HOLD_KEY) || 'null');
  } catch (_e) {
    return null;
  }
}

function writeHold(hold) {
  try {
    sessionStorage.setItem(HOLD_KEY, JSON.stringify(hold));
  } catch (_e) {}
}

function clearHold() {
  try {
    sessionStorage.removeItem(HOLD_KEY);
  } catch (_e) {}
}

/** @param {number} ms */
function formatClock(ms) {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

/**
 * Lightweight money formatter that mirrors Shopify's default.
 * Reads currency code from window.Shopify if available; falls back to USD.
 * @param {number} cents
 */
function formatMoney(cents) {
  try {
    const code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
    }).format(cents / 100);
  } catch (_e) {
    return '$' + (cents / 100).toFixed(2);
  }
}

function init() {
  new CartDrawerOverlayManager();
  new UpgradeHold();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
